import { trackingConfig } from '../data/shopify';

type Primitive = string | number | boolean | undefined;
type MetaContent = { id: string; quantity: number; item_price?: number };
type EventPayload = Record<string, Primitive | string[] | MetaContent[]>;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    fbq?: ((...args: unknown[]) => void) & { callMethod?: unknown; queue?: unknown[]; loaded?: boolean; version?: string };
    _fbq?: unknown;
  }
}

// Internal event name → Meta standard event. Unmapped events go out as trackCustom.
const META_STANDARD: Record<string, string> = {
  page_view: 'PageView',
  view_content: 'ViewContent',
  add_to_cart: 'AddToCart',
  // InitiateCheckout is sent by Omega (Pixel + Conversions API) when Shopify checkout opens.
  // The storefront sends its own pre-checkout step as the custom event "begin_checkout",
  // so InitiateCheckout is never counted twice.
  contact_submit: 'Contact',
  // Purchase is fired by Shopify's Meta channel (same Pixel ID) on the thank-you page — never from this storefront,
  // so it can't be counted twice.
};

// Parameters Meta understands for standard events. Everything else stays in dataLayer only.
const META_PARAMS = ['value', 'currency', 'content_ids', 'content_type', 'content_name', 'content_category', 'contents', 'num_items'];

let pixelInitialized = false;

const pixelId = () => trackingConfig.metaPixelId.trim();

/** Loads the Meta Pixel base code once. PageView is sent by the router on every route change. */
export function initMetaPixel() {
  if (pixelInitialized || !pixelId() || typeof window === 'undefined') return;
  pixelInitialized = true;
  /* eslint-disable */
  if (!window.fbq) {
    (function (f: Window, b: Document, e: string, v: string) {
      const n: any = function (...args: unknown[]) {
        n.callMethod ? n.callMethod(...args) : n.queue.push(args);
      };
      f.fbq = n;
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const t = b.createElement(e) as HTMLScriptElement;
      t.async = true;
      t.src = v;
      const s = b.getElementsByTagName(e)[0];
      if (s?.parentNode) s.parentNode.insertBefore(t, s);
      else b.head.appendChild(t);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  }
  /* eslint-enable */
  // Automatic button-click events off: only the clean, deliberate funnel events below reach Ads Manager.
  window.fbq?.('set', 'autoConfig', false, pixelId());
  window.fbq?.('init', pixelId());
}

function metaParams(payload: EventPayload) {
  const out: Record<string, unknown> = {};
  for (const k of META_PARAMS) if (payload[k] !== undefined) out[k] = payload[k];
  return out;
}

/**
 * Pushes every event to dataLayer and to Meta Pixel with a unique eventID, so a server-side
 * Conversions API call sending the same eventID is deduplicated by Meta.
 */
export function track(event: string, payload: EventPayload = {}) {
  const eventId = `${event}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  if (typeof window === 'undefined') return eventId;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, eventId, ...payload });

  if (pixelId() && window.fbq) {
    const standard = META_STANDARD[event];
    if (standard) {
      window.fbq('track', standard, standard === 'PageView' ? {} : metaParams(payload), { eventID: eventId });
    } else {
      window.fbq('trackCustom', event, payload, { eventID: eventId });
    }
  }
  return eventId;
}

/**
 * Navigate away (e.g. to Shopify checkout) only after the Pixel has had time to send
 * the last events. An immediate redirect can cancel in-flight requests.
 */
export function navigateAfterTracking(url: string, delayMs = 350) {
  window.setTimeout(() => window.location.assign(url), pixelId() ? delayMs : 0);
}
